import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { ActionsUI } from '@frontend/contact-actions';
import { useCustomContactFormSlidePanel } from '@frontend/create-contact-panel';
import { i18next, useTranslation } from '@frontend/i18n';
import { sanitizePhoneNumber } from '@frontend/phone-numbers';
import { CallDetailsContext, RecentCallRecord } from '@frontend/phone-panels';
import { useContactPanelShallowStore } from '@frontend/shared';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { useAlert } from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import { getActionIcon } from '../../ui-utils';
import { Action } from '../all-calls/shared-buttons';
import { getShortHandName } from './utils';

export type HistoryActionsGetterProps = {
  index: number;
  rowData: RecentCallRecord;
  triggerCall: () => void;
  addTag: () => void;
};

export const useHistoryActionsGetter = ({
  searchQuery,
  downloadMedia,
  deleteMedia,
  downloadAccessMap,
  deleteAccessMap,
  onAfterAction = () => {},
}: {
  searchQuery: string;
  downloadMedia: (rowData: RecentCallRecord) => void;
  deleteMedia: (rowData: RecentCallRecord) => void;
  downloadAccessMap: Record<string, boolean>;
  deleteAccessMap: Record<string, boolean>;
  onAfterAction?: (rowData: RecentCallRecord, index: number) => void;
}) => {
  const { onClick: messageOnClick } = ActionsUI.actions.useMessageAction();

  const { setPersonId } = useContactPanelShallowStore('setPersonId', 'personId');
  const { setShow } = useSlidePanelShallowStore<CallDetailsContext>('setShow');
  const queryClient = useQueryClient();

  const { t } = useTranslation('calls');
  const { setShow: openPanel } = useCustomContactFormSlidePanel();
  const alert = useAlert();

  return useCallback(
    ({ index, rowData, triggerCall, addTag }: HistoryActionsGetterProps) =>
      getHistoryActions({
        rowData,
        triggerCall,
        triggerMessage: () => {
          messageOnClick({
            personId: rowData.person?.personId ?? '',
            threadGroupId: rowData.locationId ?? '',
            personPhone: rowData.direction === 'inbound' ? rowData.callerNumber : rowData.dialedNumber,
            locationPhone: rowData.direction === 'outbound' ? rowData.callerNumber : rowData.dialedNumber,
          });
        },
        viewProfile: () => {
          if (!!rowData.person?.personId) {
            setPersonId(rowData.person?.personId);
            setShow(true, 'contact');
          }
        },
        createContact: () => {
          const number = rowData.direction === 'inbound' ? rowData.callerNumber : rowData.dialedNumber;

          const digitsOnly = number ? sanitizePhoneNumber(number) : '';
          const hasCountryCode = digitsOnly.startsWith('1');

          openPanel({
            show: true,
            context: {
              mode: 'create',
              person: {
                MobilePhone: hasCountryCode ? digitsOnly.slice(1) : digitsOnly,
              },
              locationId: rowData.locationId,
              onSave: () => {
                alert.success(t('Contact created successfully.'));

                // This line doesn't quite accomplish anything because the logs do not update with the newly created contact
                queryClient.invalidateQueries({ queryKey: queryKeys.recentCallLogs(searchQuery) });
                queryClient.invalidateQueries({
                  predicate: (query) => query.queryKey.includes('contacts'),
                });
              },
              onError: () => {
                alert.error(t('There was an error creating your contact. Please try again.'));
              },
            },
          });
        },
        addTag,
        downloadMedia: () => {
          downloadMedia(rowData);
        },
        deleteMedia: () => {
          deleteMedia(rowData);
        },
        hasDownloadAccess: rowData.locationId ? downloadAccessMap[rowData.locationId] : false,
        hasDeleteAccess: rowData.locationId ? deleteAccessMap[rowData.locationId] : false,
      }).map((action) => ({
        ...action,
        onClick: (...rest) => {
          action.onClick?.(...rest);
          onAfterAction(rowData, index);
        },
      })) as Action[],
    [
      alert,
      deleteAccessMap,
      deleteMedia,
      downloadAccessMap,
      downloadMedia,
      messageOnClick,
      onAfterAction,
      openPanel,
      queryClient,
      searchQuery,
      setPersonId,
      setShow,
      t,
    ]
  );
};

export const getHistoryActions = ({
  rowData,
  triggerCall,
  triggerMessage,
  viewProfile,
  createContact,
  addTag,
  downloadMedia,
  deleteMedia,
  hasDownloadAccess,
  hasDeleteAccess,
}: {
  rowData: RecentCallRecord;
  triggerCall: () => void;
  triggerMessage: () => void;
  viewProfile: () => void;
  createContact: () => void;
  addTag: () => void;
  downloadMedia: () => void;
  deleteMedia: () => void;
  hasDownloadAccess: boolean;
  hasDeleteAccess: boolean;
}) => {
  const actions: Action[] = [
    {
      Icon: () => getActionIcon({ iconName: 'phone' }),
      trackingId: 'phone-portal-weave2.0-calls-btn-tablerowaction-call',
      label: `${i18next.t('Call')} ${getShortHandName(rowData)}`,
      id: 'call-action',
      onClick: triggerCall,
    },
    {
      Icon: () => getActionIcon({ iconName: 'message' }),
      trackingId: 'phone-portal-weave2.0-calls-btn-tablerowaction-message',
      label: `${i18next.t('Message')} ${getShortHandName(rowData)}`,
      id: 'message-action',
      onClick: triggerMessage,
    },
    {
      Icon: () => getActionIcon({ iconName: 'user' }),
      hide: !rowData.person?.personId,
      trackingId: 'phone-portal-weave2.0-calls-btn-tablerowaction-viewprofile',
      label: i18next.t('View Profile'),
      id: 'view-profile-action',
      onClick: viewProfile,
    },
    {
      Icon: () => getActionIcon({ iconName: 'user-management' }),
      hide: !!rowData.person?.personId,
      trackingId: 'phone-portal-weave2.0-calls-btn-tablerowaction-createcontact',
      label: i18next.t('Create Contact'),
      id: 'create-contact-action',
      onClick: createContact,
    },
    {
      Icon: () => getActionIcon({ iconName: 'label' }),
      label: i18next.t('Add Tag'),
      id: 'add-tag-action',
      trackingId: 'phone-portal-weave2.0-recentcalls-btn-tablerowaction-tagrecord',
      onClick: addTag,
    },
    {
      Icon: () => getActionIcon({ iconName: 'download-small' }),
      trackingId: 'phone-portal-weave2.0-calls-btn-tablerowaction-download',
      label: i18next.t('Download Media'),
      id: 'download-media-action',
      onClick: downloadMedia,
      hide:
        !rowData.channelId ||
        !rowData.locationId ||
        // !hasAccesstoCallRecordingMap[rowData.locationId] ||
        !hasDownloadAccess ||
        !rowData.recordingPath,
    },
    {
      Icon: (props) => getActionIcon({ iconName: 'trash-small', color: props.color }),
      trackingId: 'phone-portal-weave2.0-calls-btn-tablerowaction-delete',
      label: i18next.t('Delete Media'),
      id: 'delete-media-action',
      destructive: true,
      onClick: deleteMedia,
      hide:
        !rowData.channelId ||
        !rowData.locationId ||
        // !hasDeleteAccesstoCallRecordingMap[rowData.locationId] ||
        !hasDeleteAccess ||
        !rowData.recordingPath,
    },
  ];

  return actions;
};
